import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useSearch } from './useSearch';
import type { SearchHit, SearchHitKind } from './types';

/**
 * Universal-search dropdown -- slice-307. Ported from POC1
 * {@code features/search/GlobalSearch.tsx}.
 *
 * <p>Hosts in the AppHeader between the title and the user actions. Operator types,
 * suggestions appear grouped by kind. Keyboard: ↑↓ to move through ALL hits (across
 * groups), Enter to navigate, Escape to close. Ctrl/Cmd+K from anywhere focuses the
 * box; "/" focuses when not already typing in an input.
 *
 * <p>NavSearch (in the sidebar) and GlobalSearch (here) coexist on purpose:
 * NavSearch finds screens by name; GlobalSearch finds records.
 */

const KIND_LABEL: Record<SearchHitKind, string> = {
  order: 'Orders',
  receipt: 'Receipts',
  item: 'Items',
  location: 'Locations',
  customer: 'Customers',
};

const KIND_ORDER: SearchHitKind[] = ['order', 'receipt', 'item', 'location', 'customer'];

// unisearch (22 Oct): shown under a group the BE cut short (statement timeout). Items is the
// usual one -- the description-contains half scans the whole item master on big owners (JWP).
const PARTIAL_NOTE: Record<SearchHitKind, string> = {
  order: 'Order search took too long — results may be incomplete.',
  receipt: 'Receipt search took too long — results may be incomplete.',
  item: 'Description search took too long — use Item Search.',
  location: 'Location search took too long — results may be incomplete.',
  customer: 'Customer search took too long — results may be incomplete.',
};

const KIND_ACCENT: Record<SearchHitKind, string> = {
  order: 'bg-phblue-100 text-phblue-800',
  receipt: 'bg-green-100 text-green-800',
  item: 'bg-amber-100 text-amber-800',
  location: 'bg-purple-100 text-purple-800',
  customer: 'bg-slate-100 text-slate-800',
};

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data, isFetching } = useSearch(query);
  const hits = useMemo(() => data?.hits ?? [], [data]);
  const partial = useMemo(() => new Set<SearchHitKind>(data?.partial ?? []), [data]);

  // Ctrl+K / Cmd+K focus shortcut; "/" focuses from anywhere non-input.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const cmdK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      const target = e.target as HTMLElement | null;
      const typingElsewhere =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);
      const slashInvoke = e.key === '/' && !typingElsewhere;
      if (cmdK || slashInvoke) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Click-outside closes.
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  useEffect(() => {
    setSelected(0);
  }, [data?.query]);

  const grouped = useMemo(() => {
    const m = new Map<SearchHitKind, SearchHit[]>();
    for (const h of hits) {
      const arr = m.get(h.kind) ?? [];
      arr.push(h);
      m.set(h.kind, arr);
    }
    // A partial kind keeps its group (even with no hits) so its note has somewhere to show.
    return KIND_ORDER.filter((k) => m.has(k) || partial.has(k)).map(
      (k) => [k, m.get(k) ?? []] as const,
    );
  }, [hits, partial]);

  // Flat ordered list matches the visual order so ↑↓ moves through all hits across groups.
  const flat = useMemo(() => grouped.flatMap(([, arr]) => arr), [grouped]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      inputRef.current?.blur();
      setOpen(false);
      return;
    }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = flat[selected];
      if (hit) goTo(hit);
    }
  }

  function goTo(hit: SearchHit) {
    navigate(hit.href);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  }

  const showDropdown = open && query.trim().length >= 2;
  const showEmpty = showDropdown && !isFetching && hits.length === 0 && partial.size === 0;

  return (
    <div ref={containerRef} className="relative max-w-xl flex-1">
      <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-slate-400">
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
        </svg>
      </span>
      <input
        ref={inputRef}
        type="text"
        placeholder="Search orders, items, receipts, locations…  (Ctrl+K)"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (query.length >= 2) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className="w-full rounded border border-transparent bg-phblue-600/40 py-1 pl-7 pr-7 text-xs text-white placeholder-phblue-200 focus:border-phblue-300 focus:bg-white focus:text-slate-800 focus:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-phblue-400"
      />
      {query && (
        <button
          onClick={() => {
            setQuery('');
            inputRef.current?.focus();
          }}
          className="absolute inset-y-0 right-1 px-2 text-xs text-phblue-200 hover:text-white"
          aria-label="Clear"
          title="Clear"
          type="button"
        >
          ✕
        </button>
      )}

      {showDropdown && (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-[70vh] overflow-auto rounded-md border border-slate-200 bg-white text-slate-800 shadow-lg">
          {isFetching && hits.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">Searching…</div>
          )}

          {showEmpty && (
            <div className="px-3 py-3 text-xs text-slate-500">
              No matches for <span className="font-mono">{query}</span>.
            </div>
          )}

          {grouped.map(([kind, items]) => (
            <div key={kind}>
              <div className="sticky top-0 border-b bg-slate-50 px-3 py-1 text-[10px] uppercase tracking-wide text-slate-500">
                {KIND_LABEL[kind]}
              </div>
              {items.map((hit) => {
                const flatIdx = flat.indexOf(hit);
                const isSel = flatIdx === selected;
                return (
                  <button
                    key={`${hit.kind}-${hit.id}`}
                    onMouseEnter={() => setSelected(flatIdx)}
                    onClick={() => goTo(hit)}
                    type="button"
                    className={clsx(
                      'flex w-full items-start gap-3 border-b border-slate-50 px-3 py-2 text-left text-sm',
                      isSel ? 'bg-phblue-50' : 'hover:bg-slate-50',
                    )}
                  >
                    <span
                      className={clsx(
                        'mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
                        KIND_ACCENT[hit.kind],
                      )}
                    >
                      {hit.kind}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono font-medium text-slate-800">
                        {hit.label}
                      </div>
                      {hit.detail && (
                        <div className="truncate text-xs text-slate-500">{hit.detail}</div>
                      )}
                    </div>
                  </button>
                );
              })}
              {partial.has(kind) && (
                <div
                  className="border-b border-slate-50 px-3 py-1.5 text-xs italic text-amber-700"
                  data-testid={`search-partial-${kind}`}
                >
                  {PARTIAL_NOTE[kind]}
                </div>
              )}
            </div>
          ))}

          <div className="flex items-center justify-between border-t px-3 py-1.5 text-[10px] text-slate-400">
            <span>↑↓ to navigate · Enter to open · Esc to close</span>
            <span>
              {hits.length} result{hits.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
